import { useMemo, useCallback, useRef } from "react"
import { useClient } from "wagmi"
import { useQueryClient } from "@tanstack/react-query"
import { useUserTicketsQuery } from "@/Overtime/queries/useUserTicketsQuery"
import type { SupportedNetwork } from "@/Overtime/types/network"
import { getCollateral } from "@/Overtime/utils/collaterals"
import useChainProviderHook from "./useChainProvider"

interface UseUnredeemedOvertimeTicketsProps {
  address: string | undefined
  selectedCollateralIndex?: number
  enabled?: boolean
}

interface UseUnredeemedOvertimeTicketsReturn {
  data: {
    inBets: number
    toPayout: number
    totalBets: number
    totalBetAmount: number
    totalPayoutAmount: number
  }
  isFetching: boolean
  refetch: () => void
  invalidateCache: () => void
}

export const useUnredeemedOvertimeTickets = ({
  address,
  selectedCollateralIndex,
  enabled = true,
}: UseUnredeemedOvertimeTicketsProps): UseUnredeemedOvertimeTicketsReturn => {
  const { isOvertimeChain, chainId } = useChainProviderHook()
  const client = useClient()
  const queryClient = useQueryClient()

  // Get selected collateral name for filtering
  const selectedCollateral = useMemo(() => {
    if (selectedCollateralIndex === undefined || !chainId) {
      return undefined
    }
    return getCollateral(chainId as SupportedNetwork, selectedCollateralIndex)
  }, [selectedCollateralIndex, chainId])

  // Cache processed data to avoid recalculation
  const processedDataRef = useRef<{
    data: any
    timestamp: number
    address: string
    chainId: number
    selectedCollateral: string | undefined
  } | null>(null)

  const {
    data: overtimeTickets,
    isFetching,
    refetch,
  } = useUserTicketsQuery(
    address || "",
    { networkId: chainId as SupportedNetwork, client },
    {
      enabled: enabled && !!address && isOvertimeChain,
      placeholderData: [],
      // Add caching optimizations
      staleTime: 30000, // 30 seconds
      gcTime: 300000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  // Memoized data processing with cache validation
  const { inBets, toPayout, totalBets, totalBetAmount, totalPayoutAmount } =
    useMemo(() => {
      // Check if we can use cached data
      if (
        processedDataRef.current &&
        processedDataRef.current.address === address &&
        processedDataRef.current.chainId === chainId &&
        processedDataRef.current.selectedCollateral === selectedCollateral &&
        processedDataRef.current.timestamp > Date.now() - 10000 && // 10 second cache
        processedDataRef.current.data
      ) {
        return processedDataRef.current.data
      }

      if (!overtimeTickets) {
        const defaultData = {
          inBets: 0,
          toPayout: 0,
          totalBets: 0,
          totalBetAmount: 0,
          totalPayoutAmount: 0,
        }

        // Cache default data
        processedDataRef.current = {
          data: defaultData,
          timestamp: Date.now(),
          address: address || "",
          chainId: chainId || 0,
          selectedCollateral,
        }

        return defaultData
      }

      // Filter tickets by selected collateral if specified
      const filteredTickets = selectedCollateral
        ? overtimeTickets.filter(
            ticket => ticket.collateral === selectedCollateral
          )
        : overtimeTickets

      // Process data efficiently
      const result = filteredTickets.reduce(
        (acc, ticket) => {
          const buyInAmount = Number(ticket.buyInAmount || 0)
          const payoutAmount = Number(ticket.payout || 0)

          acc.totalBets += 1
          acc.totalBetAmount += buyInAmount

          if (!ticket.isResolved) {
            acc.inBets += buyInAmount
          }

          if (ticket.isResolved && ticket.isUserTheWinner) {
            acc.totalPayoutAmount += payoutAmount
          }

          if (ticket.isClaimable) {
            acc.toPayout += payoutAmount
          }

          return acc
        },
        {
          inBets: 0,
          toPayout: 0,
          totalBets: 0,
          totalBetAmount: 0,
          totalPayoutAmount: 0,
        }
      )

      // Cache processed data
      processedDataRef.current = {
        data: result,
        timestamp: Date.now(),
        address: address || "",
        chainId: chainId || 0,
        selectedCollateral,
      }

      return result
    }, [overtimeTickets, address, chainId, selectedCollateral])

  // Optimized cache invalidation
  const invalidateCache = useCallback(() => {
    processedDataRef.current = null
    queryClient.invalidateQueries({
      queryKey: ["userTickets", chainId, address],
    })
  }, [queryClient, chainId, address])

  // Debounced refetch to prevent rapid successive calls
  const debouncedRefetch = useCallback(() => {
    const timeoutId = setTimeout(() => {
      refetch()
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [refetch])

  return {
    data: {
      inBets,
      toPayout,
      totalBets,
      totalBetAmount,
      totalPayoutAmount,
    },
    isFetching,
    refetch: debouncedRefetch,
    invalidateCache,
  }
}
